import React from 'react';
import type {
  ILoggedOutState,
  LoggedOutStateValues,
} from '../../connect/LoggedOutState';
import {getTestmodeLogger} from '../../utils/getLogger';
import {useAuthContext} from '../AuthContext/AuthContext';
import type {ConnectElementImportKeys} from '../../connect/ConnectJSInterface/ConnectElementList';

type Props = {
  loggedOutStateObserver: ILoggedOutState;
  connectedElement: ConnectElementImportKeys;
};

type ILoggedOutContext = {
  loggedOut: boolean;
};

const defaultLogoutContext: ILoggedOutContext = {
  loggedOut: false,
};

export const LoggedOutContext =
  React.createContext<ILoggedOutContext>(defaultLogoutContext);
LoggedOutContext.displayName = 'LogggedOutContext';

export const useOnboardingContext = () => React.useContext(LoggedOutContext);

export const LoggedOutProvider: React.FC<Props> = ({
  children,
  loggedOutStateObserver,
  connectedElement,
}) => {
  const [loggedOutState, setLoggedOutState] =
    React.useState<LoggedOutStateValues>(loggedOutStateObserver.values);
  const {authInfo} = useAuthContext();

  // Log a console warning when the logged out state changes to false for platforms in
  React.useEffect(() => {
    // Wait for auth to complete
    if (!authInfo) {
      return;
    }

    if (loggedOutState.loggedOut) {
      const testmodeLogger = getTestmodeLogger(!authInfo?.livemode);
      testmodeLogger.warn(
        `Because you've called the 'logout' function, the '${connectedElement}' component will stop rendering.`,
      );
    }
  }, [authInfo, loggedOutState.loggedOut, connectedElement]);

  React.useEffect(() => {
    const observer = () => {
      setLoggedOutState(loggedOutStateObserver.values);
    };
    loggedOutStateObserver.registerObserver(observer);
    return () => {
      loggedOutStateObserver.unregisterObserver(observer);
    };
  }, [loggedOutStateObserver]);

  const renderContent = () => {
    // If the platform has called the logout function, we will not render any content
    if (loggedOutState.loggedOut) {
      return null;
    }

    return children;
  };

  return (
    <LoggedOutContext.Provider
      value={{
        loggedOut: loggedOutState.loggedOut,
      }}
    >
      {renderContent()}
    </LoggedOutContext.Provider>
  );
};
